import React, { useReducer, useMemo } from "react";
import classNames from "classnames";
import { Loading } from "@/components";
import { i18n } from "@/utils";
import { ImageSort, UserImage, Color } from "./mini-me";
import ChoosePrevieOption from "./choose-preview-option";
import styles from "./style.less";

interface ChooseOptionProps {
  /** 强制更新 */
  count: number;
  /** 列表图片数据 */
  option: ImageSort;
  /** 底图宽度 */
  bottomImageWidth: number;
  /** 头像 */
  avatar: UserImage;
  /** 带 path 的图片 */
  pathImage: { [propName: number]: string };
  /** 已选择的肤色 */
  selectedColor: Color;
  /** 触发点击事件 */
  handleOptionClick: (option: ImageSort) => void;
}

const ChooseOption: React.FC<ChooseOptionProps> = ({
  count,
  option,
  bottomImageWidth,
  avatar,
  pathImage,
  selectedColor,
  handleOptionClick,
}) => {
  /** forceUpdate */
  const [ignored, forceUpdate] = useReducer((x) => x + 1, 0);

  /** 底图地址、ar底图地址、ar底图间隔 */
  const [bodyImageUrl, arBodyImageUrl, AR_PADDING = 0] = useMemo(() => {
    const bodyItem = option.origin.images.find(
      (item) => item.color === selectedColor.name
    );
    const arBodyItem = option.origin.arImages?.find(
      (item) => item.color === selectedColor.name
    );
    const body = bodyItem ? bodyItem.url : "";
    const arBody = arBodyItem ? arBodyItem.url : "";

    const padding = arBodyItem ? 15 : 0;

    return [body, arBody, padding];
  }, [option.origin.images, option.origin.arImages, selectedColor]);

  /** 是否正在加载 */
  const isLoading = (() => {
    for (let i in option.loading) {
      if (option.loading[i]) {
        return true;
      }
    }
    return false;
  })();

  const bottomImageWidthWithGif = useMemo(
    () => bottomImageWidth - AR_PADDING,
    [bottomImageWidth]
  );

  // 数据
  const [
    bottomImageHeight,
    avatarLeft,
    avatarTop,
    avatarWidth,
    avatarHeight,
    avatarScale,
  ] = useMemo(() => {
    // 显示区域的宽度
    const bottomImageHeight =
      (option.origin.height / option.origin.width) * bottomImageWidthWithGif;

    // 如果人脸识别成功就适配脸部区域
    if (
      avatar.faceRecognizeData &&
      avatar.width &&
      avatar.height &&
      option.origin.face
    ) {
      // 获取人脸识别数据
      const { faceRect } = avatar.faceRecognizeData;
      const { face } = option.origin;
      const faceWidth =
        (face.width / option.origin.width) * bottomImageWidthWithGif;
      const faceHeight =
        (face.height / option.origin.height) * bottomImageHeight;

      const avatarScale = Math.min(
        faceWidth / faceRect.width,
        faceHeight / faceRect.height
      );
      const heightDistance =
        (avatar.height - faceRect.top - faceRect.height) * avatarScale;

      const avatarWidth = avatar.width * avatarScale;
      const avatarWidthPecent = avatarWidth / bottomImageWidthWithGif;
      const avatarHeight = avatar.height * avatarScale;
      const avatarHeightPecent = avatarHeight / bottomImageHeight;

      const avatarTop =
        ((face.top + face.height) / option.origin.height) * bottomImageHeight +
        heightDistance;
      const avatarLeft =
        (face.left / option.origin.width) * bottomImageWidthWithGif -
        faceRect.left * avatarScale +
        avatarWidth / 2;
      const avatarLeftPrecent = avatarLeft / bottomImageWidthWithGif;
      const avatarTopPrecent = avatarTop / bottomImageHeight;

      return [
        bottomImageHeight,
        `${avatarLeftPrecent * 100}%`,
        `${avatarTopPrecent * 100}%`,
        `${avatarWidthPecent * 100}%`,
        `${avatarHeightPecent * 100}%`,
        avatarScale,
      ];
    } else {
      const avatarLeft =
        ((option.origin.avatar.left + option.origin.avatar.width / 2) /
          option.origin.width) *
        bottomImageWidthWithGif;
      const avatarLeftPrecent = avatarLeft / bottomImageWidthWithGif;
      const avatarTop =
        ((option.origin.avatar.top + option.origin.avatar.height) /
          option.origin.height) *
        bottomImageHeight;
      const avatarTopPrecent = avatarTop / bottomImageHeight;
      const avatarWidth =
        (option.origin.avatar.width / option.origin.width) *
        bottomImageWidthWithGif;
      const avatarWidthPecent = avatarWidth / bottomImageWidthWithGif;
      const avatarHeight =
        (option.origin.avatar.height / option.origin.height) *
        bottomImageHeight;
      const avatarHeightPecent = avatarHeight / bottomImageHeight;
      return [
        bottomImageHeight,
        `${avatarLeftPrecent * 100}%`,
        `${avatarTopPrecent * 100}%`,
        `${avatarWidthPecent * 100}%`,
        `${avatarHeightPecent * 100}%`,
        1,
      ];
    }
  }, [bottomImageWidthWithGif, option, count]);

  /** 加载完毕回调 */
  const handleLoaded = (image?: string) => {
    if (image) {
      option.loading[image] = false;
    } else {
      for (let i in option.loading) {
        option.loading[i] = false;
      }
    }
    forceUpdate();
  };

  return (
    <div
      className={classNames(styles.chooseOption, {
        [styles.arChooseOption]: !!arBodyImageUrl,
      })}
      onClick={() => handleOptionClick(option)}
      style={{
        height: bottomImageHeight + AR_PADDING,
      }}
    >
      {isLoading && (
        <div className={styles.imageLoading}>
          <Loading size={30} />
        </div>
      )}
      {option.isPathImage ? (
        pathImage[option.id] && (
          <img
            className={styles.pathImage}
            src={pathImage[option.id]}
            onLoad={() => handleLoaded()}
          />
        )
      ) : (
        <ChoosePrevieOption
          forceUpdate={forceUpdate}
          bodyUrl={arBodyImageUrl ? arBodyImageUrl : bodyImageUrl}
          option={option}
          avatar={avatar}
          selectedColor={selectedColor}
          avatarWidth={avatarWidth}
          avatarHeight={avatarHeight}
          avatarLeft={avatarLeft}
          avatarTop={avatarTop}
          avatarScale={avatarScale}
          bottomImageHeight={bottomImageHeight}
          bottomImageWidth={bottomImageWidthWithGif}
        />
      )}
      {!isLoading && (
        <div className={styles.chooseOptionTip}>
          {i18n.format("modules.mini.me.tab.and.edit")}
        </div>
      )}
      {arBodyImageUrl && (
        <div className={styles.arStatic}>
          <div className={styles.imageContent}>
            <ChoosePrevieOption
              forceUpdate={forceUpdate}
              bodyUrl={bodyImageUrl}
              option={option}
              avatar={avatar}
              selectedColor={selectedColor}
              avatarWidth={avatarWidth}
              avatarHeight={avatarHeight}
              avatarLeft={avatarLeft}
              avatarTop={avatarTop}
              avatarScale={avatarScale}
              bottomImageHeight={bottomImageHeight}
              bottomImageWidth={bottomImageWidthWithGif}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChooseOption;
